import { apiClient } from '../api/client';
import { applyKeywordPlaceholders } from './urlTemplates';
import { ScrapingTask } from '../hooks/useScrapingTasks';

export type CaptchaTask = ScrapingTask & {
  challenge_screenshot_url?: string | null;
};

export { taskNeedsCaptchaAction } from './captchaTask';

export function enrichCaptchaTaskFromResults(
  task: ScrapingTask,
  results: Array<{
    url?: string;
    screenshot_url?: string;
    extracted_data?: {
      blocked_url?: string;
      screenshot_url?: string;
      blocking_detected?: boolean;
      blocking_type?: string;
    };
  }>
): CaptchaTask {
  const blockedResult = results.find(
    r =>
      r.extracted_data?.blocked_url ||
      (r.extracted_data?.blocking_detected && r.extracted_data?.blocking_type === 'captcha')
  );
  if (!blockedResult) {
    return task;
  }

  const blockedUrl = blockedResult.extracted_data?.blocked_url || blockedResult.url;
  const screenshotUrl =
    blockedResult.extracted_data?.screenshot_url || blockedResult.screenshot_url || null;

  return {
    ...task,
    blocked_url: blockedUrl ?? task.blocked_url,
    challenge_screenshot_url: screenshotUrl,
  };
}

export async function checkCookieStatus(domain: string): Promise<boolean> {
  try {
    const response = await apiClient.get(`/api/v1/scraper/cookies/status/${domain}`);
    return Boolean(response.data.has_cookies);
  } catch (error) {
    console.error('Failed to check cookie status:', error);
    return false;
  }
}

export function openCaptchaUrl(task: CaptchaTask): void {
  const blockedUrl = task.blocked_url;
  if (blockedUrl) {
    window.open(blockedUrl, '_blank');
    return;
  }

  const rawUrl = task.urls?.[0];
  if (!rawUrl) return;

  let finalUrl = rawUrl;
  try {
    const urlObj = new URL(rawUrl);
    const domain = urlObj.hostname;
    const keywords =
      task.keywords_text || task.product_name || task.brand_name || task.name || undefined;

    if (keywords) {
      if (finalUrl.includes('{query}')) {
        finalUrl = finalUrl.split('{query}').join(encodeURIComponent(keywords));
      }
      if (finalUrl.includes('{keyword}') || finalUrl.includes('{keywords}')) {
        finalUrl = applyKeywordPlaceholders(finalUrl, keywords);
      }
    }

    if (
      (domain === 'tokopedia.com' || domain.endsWith('.tokopedia.com')) &&
      task.input_mode === 'keywords' &&
      keywords &&
      !finalUrl.includes('q=')
    ) {
      finalUrl = `https://www.tokopedia.com/search?q=${encodeURIComponent(keywords)}`;
    }
  } catch {
    const keywords =
      task.keywords_text || task.product_name || task.brand_name || task.name || undefined;
    if (keywords) {
      if (finalUrl.includes('{query}')) {
        finalUrl = finalUrl.split('{query}').join(encodeURIComponent(keywords));
      }
      if (finalUrl.includes('{keyword}') || finalUrl.includes('{keywords}')) {
        finalUrl = applyKeywordPlaceholders(finalUrl, keywords);
      }
    }
  }

  if (typeof (window as any).chrome !== 'undefined' && (window as any).chrome.storage) {
    (window as any).chrome.storage.local.set({ authentix_task_id: task.id });
  }

  window.open(finalUrl, '_blank');
}
